import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useTri, EnteteTri } from './TriTableau';
import './ReceptionsStats.css';

// ═══════════════════════════════════════════════════════════════════════════
// Tableaux ▸ Réceptions — l'outil de management du responsable d'accueil.
//
// Combien de clients chaque réceptionnaire a EFFECTIVEMENT reçus (entrées de
// file prises en charge), ventilé par motif de visite, avec l'attente moyenne
// avant prise en charge et l'heure de la dernière réception. La ligne « Non
// affecté » expose les passages sans réceptionnaire identifié (compte sans
// fiche) : un trou de traçabilité à corriger, pas à cacher.
// Réservé aux administrateurs du Planning — l'option de menu est déjà filtrée,
// le serveur fait barrière (403).
// ═══════════════════════════════════════════════════════════════════════════

const MOTIFS = [
  { cle: 'rdv', label: 'RDV' },
  { cle: 'prise_rdv', label: 'Sans RDV' },
  { cle: 'recuperation', label: 'Récupération' },
  { cle: 'info', label: 'Informations' },
];

const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Bornes de la période contenant la date d'ancre (semaine = lundi → dimanche)
function bornesPeriode(periode, ancre) {
  const d = new Date(ancre.getFullYear(), ancre.getMonth(), ancre.getDate());
  if (periode === 'jour') return { du: toYmd(d), au: toYmd(d) };
  if (periode === 'semaine') {
    const lundi = new Date(d); lundi.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const dimanche = new Date(lundi); dimanche.setDate(lundi.getDate() + 6);
    return { du: toYmd(lundi), au: toYmd(dimanche) };
  }
  return {
    du: toYmd(new Date(d.getFullYear(), d.getMonth(), 1)),
    au: toYmd(new Date(d.getFullYear(), d.getMonth() + 1, 0)),
  };
}

const fmtJourLong = (ymd) => {
  const [a, m, j] = ymd.split('-').map(Number);
  return new Date(a, m - 1, j).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};
const fmtJourCourt = (ymd) => {
  const [a, m, j] = ymd.split('-').map(Number);
  return new Date(a, m - 1, j).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

export default function ReceptionsStats() {
  const [periode, setPeriode] = useState('jour'); // 'jour' | 'semaine' | 'mois'
  const [ancre, setAncre] = useState(() => new Date());
  const [donnees, setDonnees] = useState(null);   // { stats, arrivees_non_recues, du, au }
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const tri = useTri(null, 'asc');

  const { du, au } = bornesPeriode(periode, ancre);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const { data } = await api.get(`/planning/receptions-stats?du=${du}&au=${au}`);
      setDonnees(data);
    } catch (e) {
      console.error('[receptions-stats]', e);
      setErreur(e.response?.status === 403
        ? 'Réservé aux administrateurs du Planning.'
        : (e.response?.data?.error || 'Chargement impossible'));
      setDonnees(null);
    } finally {
      setChargement(false);
    }
  }, [du, au]);

  useEffect(() => { charger(); }, [charger]);

  const naviguer = (sens) => {
    const d = new Date(ancre);
    if (periode === 'jour') d.setDate(d.getDate() + sens);
    else if (periode === 'semaine') d.setDate(d.getDate() + 7 * sens);
    else d.setMonth(d.getMonth() + sens);
    setAncre(d);
  };

  const aujourdHui = toYmd(new Date());
  const surAujourdHui = du <= aujourdHui && aujourdHui <= au;

  const libellePeriode = periode === 'jour' ? fmtJourLong(du)
    : periode === 'semaine' ? `du ${fmtJourCourt(du)} au ${fmtJourCourt(au)}`
    : new Date(ancre.getFullYear(), ancre.getMonth(), 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const fmtAttente = (min) => {
    if (min == null) return '—';
    return min >= 60 ? `${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}` : `${min} min`;
  };
  const fmtDerniere = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return periode === 'jour' ? heure
      : `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${heure}`;
  };
  const nomDe = (s) => s.receptionnaire_id
    ? [s.prenom, s.nom].filter(Boolean).join(' ') || `Fiche #${s.receptionnaire_id}`
    : 'Non affecté';

  const stats = donnees?.stats || [];
  const lignes = tri.appliquer(stats, {
    receptionnaire: s => nomDe(s),
    rdv: s => s.rdv, prise_rdv: s => s.prise_rdv,
    recuperation: s => s.recuperation, info: s => s.info,
    total: s => s.total,
    attente: s => s.attente_moyenne_min ?? -1,
    derniere: s => s.derniere_reception || '',
  });
  const totaux = MOTIFS.reduce((acc, m) => ({ ...acc, [m.cle]: stats.reduce((n, s) => n + s[m.cle], 0) }), {});
  const totalGeneral = stats.reduce((n, s) => n + s.total, 0);

  return (
    <div className="rst">
      <div className="rst-entete">
        <h2>🧾 Réceptions par réceptionnaire</h2>
        <div className="rst-outils">
          <div className="rst-filtres">
            {[['jour', 'Jour'], ['semaine', 'Semaine'], ['mois', 'Mois']].map(([cle, lbl]) => (
              <button key={cle} type="button" className={`rst-filtre${periode === cle ? ' is-active' : ''}`}
                onClick={() => setPeriode(cle)}>{lbl}</button>
            ))}
          </div>
          <div className="rst-nav">
            <button type="button" className="rst-nav-btn" onClick={() => naviguer(-1)} title="Période précédente">◀</button>
            <span className="rst-nav-label">{libellePeriode}</span>
            <button type="button" className="rst-nav-btn" onClick={() => naviguer(1)} title="Période suivante">▶</button>
            {!surAujourdHui && (
              <button type="button" className="rst-nav-today" onClick={() => setAncre(new Date())}>Aujourd'hui</button>
            )}
          </div>
        </div>
      </div>

      {erreur && <p className="rst-note">⚠️ {erreur}</p>}
      {chargement && !donnees && <p className="rst-note">Chargement…</p>}
      {!chargement && !erreur && stats.length === 0 && (
        <p className="rst-note">Aucune réception sur cette période.</p>
      )}

      {stats.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <EnteteTri tri={tri} cle="receptionnaire">Réceptionnaire</EnteteTri>
                {MOTIFS.map(m => <EnteteTri key={m.cle} tri={tri} cle={m.cle}>{m.label}</EnteteTri>)}
                <EnteteTri tri={tri} cle="total">Total</EnteteTri>
                <EnteteTri tri={tri} cle="attente">Attente moy.</EnteteTri>
                <EnteteTri tri={tri} cle="derniere">Dernière réception</EnteteTri>
              </tr>
            </thead>
            <tbody>
              {lignes.map(s => (
                <tr key={s.receptionnaire_id ?? 'na'} className={s.receptionnaire_id ? '' : 'rst-ligne-na'}>
                  <td className="rst-nom">{nomDe(s)}</td>
                  {MOTIFS.map(m => (
                    <td key={m.cle} className="rst-num">{s[m.cle] > 0 ? s[m.cle] : <span className="rst-zero">—</span>}</td>
                  ))}
                  <td className="rst-num rst-total">{s.total}</td>
                  <td className="rst-num">{fmtAttente(s.attente_moyenne_min)}</td>
                  <td className="rst-num rst-muted">{fmtDerniere(s.derniere_reception)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="rst-pied">
                <td>Total</td>
                {MOTIFS.map(m => <td key={m.cle} className="rst-num">{totaux[m.cle] || '—'}</td>)}
                <td className="rst-num rst-total">{totalGeneral}</td>
                <td className="rst-num" colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {donnees && donnees.arrivees_non_recues > 0 && (
        <p className="rst-note">
          🙋 {donnees.arrivees_non_recues} arrivée{donnees.arrivees_non_recues > 1 ? 's' : ''} jamais
          prise{donnees.arrivees_non_recues > 1 ? 's' : ''} en charge sur la période (non comptée{donnees.arrivees_non_recues > 1 ? 's' : ''}).
        </p>
      )}
    </div>
  );
}
